'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useDataTableDefaults } from '../data/table-defaults-context';
import { ConfirmCardModal } from './confirm-card-modal';
/**
 * "Discard changes?" for a form modal (modern modules only).
 *
 * Dirty = the user typed/picked something since the modal opened: native change events
 * inside the content (trackChange) + AdaptiveSelectField via FormDirtyContext (Provider).
 * Wire it up as:
 *   <FormDirtyContext.Provider value={guard.dirtyContextValue}>
 *     <Modal onClose={guard.requestClose}>            ← ✕ / Esc / overlay
 *       <div onChangeCapture={guard.trackChange}>…</div>
 *       <ModalFooter onClickCapture={guard.interceptCancel}>
 *         <button data-modal-cancel onClick={onClose}>Cancel</button> …
 *     </Modal>
 *     {guard.dialog}
 *   </FormDirtyContext.Provider>
 * Saving closes through the page's own onClose, so it never prompts.
 */
export function useDiscardGuard({ isOpen, onClose, enabled = true }) {
    const guardChanges = useDataTableDefaults().appearance === 'modern' && enabled;
    const [isDirty, setIsDirty] = useState(false);
    const [askDiscard, setAskDiscard] = useState(false);
    const markDirty = useCallback(() => setIsDirty(true), []);
    useEffect(() => {
        if (isOpen) {
            setIsDirty(false);
            setAskDiscard(false);
        }
    }, [isOpen]);
    const requestClose = () => {
        if (guardChanges && isDirty)
            setAskDiscard(true);
        else
            onClose();
    };
    const trackChange = guardChanges
        ? (e) => {
            // Typing in a dropdown's search box is not a change (picking an option is, via the context)
            if (e.target.getAttribute('aria-autocomplete') === 'list')
                return;
            // Search / filter boxes that only narrow what is shown opt out with data-discard-ignore
            if (e.target.closest('[data-discard-ignore]'))
                return;
            setIsDirty(true);
        }
        : undefined;
    const interceptCancel = guardChanges
        ? (e) => {
            if (!isDirty || !e.target.closest('[data-modal-cancel]'))
                return;
            // Capture phase: stop the Cancel button's own onClose and ask first
            e.stopPropagation();
            e.preventDefault();
            setAskDiscard(true);
        }
        : undefined;
    const dialog = guardChanges ? (_jsx(ConfirmCardModal, { isOpen: askDiscard, onClose: () => setAskDiscard(false), onConfirm: () => { setAskDiscard(false); setIsDirty(false); onClose(); }, title: "Discard changes?", message: "You have unsaved changes. If you close now, they will be lost.", tone: "warning", confirmText: "Discard", cancelText: "Keep editing", zIndexClass: "z-[70]" })) : null;
    return { requestClose, trackChange, interceptCancel, dirtyContextValue: guardChanges ? markDirty : null, dialog };
}
